import type { ResolvedDatumTable } from "building-types/datums/datumsTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { StairBlockNodeRun, StairBlockNodeTable, StairBlockResolved } from "building-types/stairs/stairTypes";
import { StairBlockType } from "building-types/stairs/stairTypesEnum";
import type { Table } from "common/data/table";
import type { LevelInches } from "common/types/nominalNumbersTypes";
import { worldInchesTolevel } from "common/utils/nominalNumbers";
import type { PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import { getDatumZ } from "../datums";

export function stairBlockNodeTableNew(
  stairBlocks: StairBlockResolved[],
  graph: PolygonPlanarGraph,
  resolvedDatums: ResolvedDatumTable,
  levels: Table<Level>,
  riserCountToNextLevel: number,
  riserHeightActual: number,
  topOfStair: LevelInches,
  levelId: string,
): StairBlockNodeTable {
  const stairBlockNodeTable: StairBlockNodeTable = {};
  if (stairBlocks.length === 0) {
    return stairBlockNodeTable;
  }

  // with fully-hosted stair blocks this is not necessary,
  // but it is preserved as a fallback for legacy stair blocks
  // and if no datums are set on a stair block
  const baseLevel = levels[levelId];

  for (let i = 0; i < stairBlocks.length; i++) {
    let node = stairBlockNodeTable[i] || { type: stairBlocks[i].type, neighborsAbove: [], neighborsBelow: [] };
    const neighborsList = graph.polygonNeighbors(i);

    for (const neighbors of neighborsList) {
      for (const neighbor of neighbors) {
        if (neighbor === null) {
          continue;
        }
        const neighborIndex = neighbor.index;
        if (neighborIndex > i) {
          node.neighborsAbove.push(neighborIndex);
        } else {
          node.neighborsBelow.push(neighborIndex);
        }
      }
    }

    let riserLimit = riserCountToNextLevel;
    const stairBlock = stairBlocks[i];
    if (stairBlock.type === StairBlockType.RUN) {
      let specificHeight = false;
      if (stairBlock.datumVerticalLower) {
        const datum = resolvedDatums[stairBlock.datumVerticalLower];
        if (datum) {
          const datumElevation = getDatumZ(datum);
          const startRiser = Math.floor(datumElevation / riserHeightActual);
          node = {
            ...node,
            ...stairBlocks[i],
            index: i,
            bottomRiser: startRiser,
            bottomHeight: worldInchesTolevel(datumElevation, baseLevel.floorElevation),
          } as StairBlockNodeRun;
          stairBlockNodeTable[i] = node as StairBlockNodeRun;
          specificHeight = true;
        }
      }

      if (stairBlock.datumVerticalUpper) {
        const datum = resolvedDatums[stairBlock.datumVerticalUpper];
        if (datum) {
          const datumElevation = getDatumZ(datum);
          riserLimit = Math.floor(datumElevation / riserHeightActual);
          node = {
            ...node,
            ...stairBlocks[i],
            index: i,
            topRiser: riserLimit,
            topHeight: worldInchesTolevel(datumElevation, baseLevel.floorElevation),
          };
          stairBlockNodeTable[i] = node as StairBlockNodeRun;
          specificHeight = true;
        }
      } else if (node.neighborsAbove.length === 0) {
        stairBlockNodeTable[i] = node = {
          ...node,
          ...stairBlocks[i],
          index: i,
          topRiser: Math.ceil(topOfStair / riserHeightActual),
          topHeight: topOfStair,
        };
        specificHeight = true;
      }

      if (specificHeight) {
        continue;
      }
    } else {
      //StairBlockType.LANDING

      if (stairBlock.datumVertical) {
        const datum = resolvedDatums[stairBlock.datumVertical];
        if (datum) {
          const datumElevation = getDatumZ(datum);
          riserLimit = Math.ceil(datumElevation / riserHeightActual);
          stairBlockNodeTable[i] = {
            ...node,
            ...stairBlocks[i],
            index: i,
            topRiser: riserLimit,
            topHeight: worldInchesTolevel(datumElevation, baseLevel.floorElevation),
          };
          continue;
        }
      }
    }

    stairBlockNodeTable[i] = { ...node, ...stairBlocks[i], index: i, topRiser: riserCountToNextLevel };
  }
  return stairBlockNodeTable;
}
